import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { useRouteMatch } from 'react-router-dom';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import {
  UpdateField,
  AddArtistToRelease,
  UpdateReleaseStoreError,
} from '@webapp/store/release/action-creators';
import {
  SearchExternalArtistsReset,
  UpdateArtistData,
} from '@webapp/store/artist/action-creators';

// Utils
import scrollOnError from '@webapp/utils/release/scroll-on-error';
import { detailsSection } from '@webapp/utils/validators/release';

import { isRecordLabelNameValid } from '@webapp/utils/record-labels/is-record-label-name-valid';
import { validateRelease } from '@webapp/utils/validators/release';

// Types
import SimpleUser from '@common/types/user/simple-user';
import SimpleArtist from '@common/types/artist/simple-artist';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { ReleaseGenre } from '@common/types/release/release-genre';
import { ReleaseArtist } from '@common/types/release/release-artist';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';

// Services
import { RecordLabelSubscriptionType } from '@services/subscription/types/record-label-subcription';

// Components
import Box from '@components/layout/box';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import ErrorMessage from '@webapp/components/basics/error-message/index';
import CounterFixedButton from '@components/actions/counter-fixed-button';
import SummaryButton from '@components/actions/summary-button';

// Controllers
import RecordLabelCtrl from '@webapp/controllers/release/record-label';
import ArtistSelectorCtrl from '@webapp/controllers/artist/artist-selector';
import GenresSelectorCtrl from '@webapp/controllers/release/genres-selector';
import BoxHeader from '@components/layout/box-header';

interface ReleaseInfoDetailsPageProps {
  className?: string;
  stepValid: boolean;
  isMobile: boolean;
  isTablet: boolean;
  releaseId?: string;
  validRecordLabel?: boolean;
  genres?: ReleaseGenre[];
  activeArtist?: ReleaseArtist;
  artistList: SimpleArtist[];
  releaseArtists: ReleaseArtist[];
  releaseTitle: string;
  labelPlan: RecordLabelSubscriptionType;
  activeArtistForReleaseSongScreen?: SimpleArtist;
  activeUser: SimpleUser;
  isReleaseValid: boolean;
  updateArtists: (artists: ReleaseArtist[]) => UpdateField<'artists'>;
  addArtistToRelease: (
    name: string,
    externalProfile?: ArtistSearchResult
  ) => AddArtistToRelease;
  goToSummary: (clientId: string, id: string) => CallHistoryMethodAction;
  updateArtist: (
    id: string,
    artistData: Partial<SimpleArtist>
  ) => UpdateArtistData;
  resetSearch: () => SearchExternalArtistsReset;
  nextSection: () => CallHistoryMethodAction;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const mapStateToProps = (state: AppState) => {
  const artistList = state.artist.myArtists;
  const releaseArtists: ReleaseArtist[] = state.release.release?.artists || [];
  let activeArtist = releaseArtists.find((a) => a.role === 'owner');

  if (!activeArtist) {
    // this is used for backward compatibility only
    activeArtist = releaseArtists.find(
      (a) => a.role === 'main' && !!artistList.find((b) => b.id === a.id)
    );
  }
  return {
    artistList,
    releaseArtists,
    activeArtist,
    stepValid: detailsSection(state),
    isReleaseValid: validateRelease(state),
    releaseId: state.release.release?.id,
    genres: state.release.release?.genres,
    validRecordLabel: isRecordLabelNameValid(
      state.release.release?.recordLabel || ''
    ),
    isMobile: state.ui.mobile,
    releaseTitle: state.release.release?.title || '',
    labelPlan: ((state.recordLabels.active?.id &&
      state.subscription.label[
        state.recordLabels.active.id
      ]?.plan.toUpperCase()) ||
      'FREE') as RecordLabelSubscriptionType,
    activeArtistForReleaseSongScreen: state.artist.active,
    activeUser: state.user.profile as SimpleUser,
    isTablet: state.ui.tablet,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  nextSection: () => dispatch(push('./dates')),
  updateArtists: (artists: ReleaseArtist[]) =>
    dispatch(new UpdateField<'artists'>('artists', artists)),
  addArtistToRelease: (name: string, externalProfile?: ArtistSearchResult) =>
    dispatch(new AddArtistToRelease(name, externalProfile)),
  resetSearch: () => dispatch(new SearchExternalArtistsReset()),
  updateArtist: (id: string, artistData: Partial<SimpleArtist>) =>
    dispatch(new UpdateArtistData(id, artistData)),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
  goToSummary: (clientId: string, id: string) =>
    dispatch(push(`/release/${clientId}/${id || ''}/preview`)),
});

export const ReleaseInfoDetailsPage: React.FC<ReleaseInfoDetailsPageProps> = ({
  releaseArtists,
  isTablet,
  ...props
}) => {
  const [genreError, setGenreError] = React.useState(false);
  const [validateErrors, setValidateErrors] = React.useState(0);
  const [contact, setContact] = React.useState<ContactInfo>();
  const [showError, setShowError] = React.useState<React.ReactNode>();
  const [role, setRole] = React.useState<string>('');
  const [name, setName] = React.useState<string>('');
  const match = useRouteMatch<{
    artistId: string;
    releaseId: string;
  }>();

  React.useEffect(() => {
    scrollOnError();
  }, [validateErrors]);

  const handleNextStepClick = () => {
    if (isValid()) {
      props.nextSection();
    } else {
      setValidateErrors(validateErrors + 1);
    }
  };

  const confirmArtistName = () => {
    if (name.length > 0) {
      props.addArtistToRelease(name);
      props.resetSearch();
    }
  };

  const handleNewArtist = (value: ArtistSearchResult) => {
    props.addArtistToRelease(value.name, value);
  };
  const isValid = () => {
    let valid = true;
    if (!props.genres?.length) {
      setGenreError(true);
      props.sendAnalytics(
        'Release Genres',
        role ? role : '',
        'RELEASE.MISSING_GENRE',
        props.releaseId
      );
      valid = valid && false;
    } else {
      setGenreError(false);
      valid = valid && true;
    }

    return props.stepValid && valid;
  };

  const releaseArtistsList = React.useMemo(() => {
    if (releaseArtists?.length) {
      return releaseArtists.map((releaseArtist) => {
        if (
          releaseArtist &&
          (!releaseArtist.platforms ||
            Object.keys(releaseArtist.platforms).length === 0)
        ) {
          return {
            ...releaseArtist,
            platforms: { apple: '', soundcloud: '', spotify: '' },
          };
        }
        return releaseArtist;
      });
    }
    return [];
  }, [releaseArtists]);

  if (!props.releaseId) {
    return null;
  }

  return (
    <MotionOpacity className={props.className}>
      {!props.isMobile && (
        <Typography
          className="title mb-30"
          type="subheader"
          customFont="semi-bold"
        >
          <Translate id="RELEASE.SIDEBAR.LIST.NAMES_GENRES" />
        </Typography>
      )}

      <Wrapper>
        <Box noBorderBottom>
          <ArtistSelectorCtrl
            artistList={props.artistList}
            releaseArtists={releaseArtistsList}
            onRemoveArtist={(artists) => props.updateArtists(artists)}
            onUpdateArtist={(artist) => handleNewArtist(artist)}
            activeArtist={props.activeArtist}
            handleEmailOrPhoneChange={setContact}
            contact={contact}
            showError={showError}
            setShowError={setShowError}
            setRole={setRole}
            setName={setName}
            noRoleDropdown
            onConfirmName={confirmArtistName}
          />
        </Box>
        <Box noBorderBottom noBorderTop>
          <BoxHeader
            title={<Translate id="RELEASE.NAMES_GENRES.RECORD_LABEL.TITLE" />}
          />
          <RecordLabelCtrl />
        </Box>
        <GenresSelectorCtrl
          hasError={
            genreError && (
              <ErrorMessage
                className="mt-10"
                hasBackground
                errorId={genreError ? 'RELEASE.MISSING_GENRE' : undefined}
              />
            )
          }
        />
      </Wrapper>

      <CounterFixedButton
        id="nextReleaseDetailsBtn"
        className={!isTablet ? '' : 'mt-30'}
        fixed={!isTablet}
        bottomMargin={props.isReleaseValid ? '55px' : undefined}
        isValid={props.genres?.length && props.validRecordLabel}
        currentStep={2}
        maxStep={3}
        customMaxStep={4}
        onNext={handleNextStepClick}
      />
      {props.isReleaseValid && (
        <SummaryButton
          className={!isTablet ? '' : 'mt-30'}
          fixed={!isTablet}
          onClick={() =>
            props.goToSummary(match.params.artistId, match.params.releaseId)
          }
        />
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ReleaseInfoDetailsPage)`
  margin-bottom: 100px;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-bottom: 0;
  }
`);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;
